//
// # Resize Outline Mode
//
// Drag-based mode for resizing the current cell's outline by dragging its edges.
// Outline is a per-cell property on Layout Abstract, in local cell coordinates.
//

import { model } from "GenAlphaModel";
import { ModeHandler } from "../../modes";
import { DrawTo } from "DrawCore/src/draw";
import * as style from "DrawCore/src/style";
import type { InteractionHost } from "../../interaction_host";
import type { LayoutContent } from "./content";

// Which edge or corner of the outline is being dragged
export type OutlineEdge =
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

// Tolerance in screen pixels for edge hit detection.
const EDGE_HIT_PIXELS = 20;

// Style for the outline preview while resizing
const previewStyle: style.ShapeStyle = {
  fill: style.Special.None,
  stroke: style.Color.cyan,
  linewidth: 2,
  lineDash: [100, 50],
  cap: "butt",
  join: "miter",
};

// Test if a point is near an outline edge or corner. Returns the edge/corner or null.
// Corners take priority over edges.
export function hitTestOutlineEdge(
  outline: model.Bbox,
  point: model.Point,
  toleranceSch: number
): OutlineEdge | null {
  const { min, max } = outline;

  // Distance from each edge
  const dLeft = Math.abs(point.x - min.x);
  const dRight = Math.abs(point.x - max.x);
  const dBottom = Math.abs(point.y - min.y);
  const dTop = Math.abs(point.y - max.y);

  const nearLeft = dLeft <= toleranceSch;
  const nearRight = dRight <= toleranceSch;
  const nearBottom = dBottom <= toleranceSch;
  const nearTop = dTop <= toleranceSch;

  // Corners: near two perpendicular edges simultaneously
  if (nearTop && nearLeft) return "top-left";
  if (nearTop && nearRight) return "top-right";
  if (nearBottom && nearLeft) return "bottom-left";
  if (nearBottom && nearRight) return "bottom-right";

  // Check if point is within the vertical extent (with tolerance) for left/right edges
  const inVertRange =
    point.y >= min.y - toleranceSch && point.y <= max.y + toleranceSch;
  // Check if point is within the horizontal extent (with tolerance) for top/bottom edges
  const inHorizRange =
    point.x >= min.x - toleranceSch && point.x <= max.x + toleranceSch;

  // Find the closest edge
  const closest = Math.min(dLeft, dRight, dBottom, dTop);

  // Only match if the closest edge is within tolerance.
  if (closest > toleranceSch) return null;

  // Return the closest matching edge
  if (closest === dLeft && inVertRange) return "left";
  if (closest === dRight && inVertRange) return "right";
  if (closest === dBottom && inHorizRange) return "bottom";
  if (closest === dTop && inHorizRange) return "top";

  return null;
}

// Compute the edge hit tolerance in schematic coordinates from pixel tolerance
export function edgeTolerance(editor: InteractionHost): number {
  const scale = editor.canvas.worldTransforms.schToPage.scale;
  return EDGE_HIT_PIXELS / scale;
}

// Resize outline mode: drag an edge to a new position.
// The outline is in local cell coordinates (same as abs.bbox).
export class ResizeOutline extends ModeHandler {
  private current: model.Bbox;

  constructor(
    private readonly editor: InteractionHost,
    private readonly layoutContent: LayoutContent,
    private readonly edge: OutlineEdge,
    private readonly original: model.Bbox
  ) {
    super();
    this.current = model.Bbox.clone(original);
    editor.overlays.setStatusMessage(
      `Resize Outline: drag ${edge} edge, Esc to cancel`,
      "resize-outline"
    );
  }

  static enter(
    editor: InteractionHost,
    layoutContent: LayoutContent,
    edge: OutlineEdge,
    outline: model.Bbox
  ): ResizeOutline {
    const mode = new ResizeOutline(editor, layoutContent, edge, outline);
    editor.setMode(mode);
    return mode;
  }

  override handleMouseMove = () => {
    const { editor, edge } = this;
    const pos = editor.uiState.mousePos.sch;

    const next = model.Bbox.clone(this.current);
    switch (edge) {
      case "left":
        next.min.x = Math.min(pos.x, this.current.max.x);
        break;
      case "right":
        next.max.x = Math.max(pos.x, this.current.min.x);
        break;
      case "bottom":
        next.min.y = Math.min(pos.y, this.current.max.y);
        break;
      case "top":
        next.max.y = Math.max(pos.y, this.current.min.y);
        break;
      case "top-left":
        next.min.x = Math.min(pos.x, this.current.max.x);
        next.max.y = Math.max(pos.y, this.current.min.y);
        break;
      case "top-right":
        next.max.x = Math.max(pos.x, this.current.min.x);
        next.max.y = Math.max(pos.y, this.current.min.y);
        break;
      case "bottom-left":
        next.min.x = Math.min(pos.x, this.current.max.x);
        next.min.y = Math.min(pos.y, this.current.max.y);
        break;
      case "bottom-right":
        next.max.x = Math.max(pos.x, this.current.min.x);
        next.min.y = Math.min(pos.y, this.current.max.y);
        break;
    }
    this.current = next;
    // Update local data for live preview
    this.layoutContent.data.abs.outline = next;
    return editor.requestRedraw();
  };

  override handleMouseUp = () => {
    this.commit();
  };

  private commit() {
    const { editor, original, current, layoutContent } = this;
    editor.overlays.clearStatusMessage("resize-outline");

    if (!model.Bbox.eq(original, current)) {
      // Update local data
      layoutContent.data.abs.outline = current;
      // Notify the platform
      const change = model.LayoutChange.setOutline(current);
      editor.platform.notifyUIChange(change);
    }
    editor.goUiIdle();
  }

  override abort = () => {
    // Restore original outline
    this.layoutContent.data.abs.outline = this.original;
    this.editor.overlays.clearStatusMessage("resize-outline");
    this.editor.goUiIdle();
    return this.editor.requestRedraw();
  };

  // Draw the outline preview while dragging
  override draw = (to: DrawTo): void => {
    const scale = this.editor.canvas.worldTransforms.schToPage.scale;
    to.bbox(this.current, {
      ...previewStyle,
      linewidth: previewStyle.linewidth / scale,
    });
  };
}
